/* Existing variant prices are preserved; the owned tooltip uses a body portal. */
if (!customElements.get('goatpc-vat-info')) {
  let tooltipId = 0;
  customElements.define('goatpc-vat-info', class extends HTMLElement {
    connectedCallback() {
      this.cleanupTooltip?.();
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;

      // Also allows children to finish parsing when the Theme Editor reloads a section.
      queueMicrotask(() => {
        if (controller.signal.aborted || !this.isConnected) return;
        const price = this.querySelector('.goatpc-vat-price');
        const product = this.closest('.js-product');
        if (!price || !product) return;
        const values = this.querySelectorAll('[data-goatpc-vat-variant]');
        this.initTooltip(controller.signal);
        const picker = product.querySelector('variant-picker');
        if (!picker) return;

        const showVariant = (variant) => {
          let found = false;
          values.forEach((value) => {
            const matches = variant != null
              && value.dataset.goatpcVatVariant === String(variant.id);
            value.hidden = !matches;
            found = found || matches;
          });
          price.hidden = !found;
          this.closeTooltip?.();
        };

        product.addEventListener('on:variant:change', (event) => {
          if (event.target === picker) showVariant(event.detail?.variant);
        }, { signal: controller.signal });

        // The supplied picker suppresses its custom event during incomplete selection.
        // Read its final state after its existing synchronous change handler has run.
        product.addEventListener('change', (event) => {
          if (!picker.contains(event.target)) return;
          queueMicrotask(() => {
            if (!controller.signal.aborted && 'variant' in picker) {
              showVariant(picker.variant);
            }
          });
        }, { signal: controller.signal });

        customElements.whenDefined('variant-picker').then(() => {
          if (!controller.signal.aborted && 'variant' in picker) {
            showVariant(picker.variant);
          }
        });
      });
    }

    initTooltip(signal) {
      const button = this.querySelector('.goatpc-vat-trigger');
      const tip = this.querySelector('.goatpc-vat-tooltip');
      if (!button || !tip) return;
      tip.id = `goatpc-vat-tooltip-${++tooltipId}`;
      button.setAttribute('aria-describedby', tip.id);
      const topLayer = typeof tip.showPopover === 'function';
      if (topLayer) tip.setAttribute('popover', 'manual');
      document.body.append(tip);
      button.hidden = false;
      let opened = false;
      let timer;
      let pointerType = '';
      let keyboardFocus = false;

      const on = (target, type, handler, options = {}) => {
        target.addEventListener(type, handler, { ...options, signal });
      };

      const close = () => {
        clearTimeout(timer);
        if (opened && topLayer) tip.hidePopover();
        opened = false;
        tip.hidden = true;
        button.setAttribute('aria-expanded', 'false');
      };

      const position = () => {
        if (!opened) return;
        const viewport = window.visualViewport;
        const left = (viewport?.offsetLeft || 0) + 12;
        const top = (viewport?.offsetTop || 0) + 12;
        const width = viewport?.width || document.documentElement.clientWidth;
        const height = viewport?.height || window.innerHeight;
        tip.style.width = `${Math.max(0, Math.min(320, width - 24))}px`;
        tip.style.maxHeight = `${Math.max(0, height - 24)}px`;
        const rect = button.getBoundingClientRect();
        const box = tip.getBoundingClientRect();
        const x = Math.max(left, Math.min(rect.left, left + width - 24 - box.width));
        const below = rect.bottom + 8;
        const y = below + box.height <= top + height - 24
          ? below
          : rect.top - box.height - 8;
        tip.style.left = `${x}px`;
        tip.style.top = `${Math.max(top, Math.min(y, top + height - 24 - box.height))}px`;
      };

      const open = () => {
        clearTimeout(timer);
        if (this.querySelector('.goatpc-vat-price').hidden) return;
        const style = getComputedStyle(this);
        tip.style.fontFamily = style.fontFamily;

        // Follow the actual section text colour, including theme-specific dark modes.
        const rgb = style.color.match(/[\d.]+/g)?.slice(0, 3).map(Number);
        tip.dataset.goatpcVatDark = String(
          rgb && (rgb[0] * .299 + rgb[1] * .587 + rgb[2] * .114) > 150
        );

        tip.hidden = false;
        if (!opened && topLayer) tip.showPopover();
        opened = true;
        button.setAttribute('aria-expanded', 'true');
        position();
      };

      const leave = (event) => {
        if (event.pointerType === 'touch' || keyboardFocus) return;
        clearTimeout(timer);
        timer = setTimeout(close, 120);
      };

      on(button, 'pointerdown', event => {
        pointerType = event.pointerType;
      });

      on(button, 'pointerenter', event => {
        if (event.pointerType !== 'touch') open();
      });

      on(button, 'pointerleave', leave);
      on(tip, 'pointerenter', () => clearTimeout(timer));
      on(tip, 'pointerleave', leave);

      on(button, 'focus', () => {
        keyboardFocus = button.matches(':focus-visible');
        if (keyboardFocus) open();
      });

      on(button, 'blur', () => {
        keyboardFocus = false;
        close();
      });

      on(button, 'click', event => {
        if (event.detail !== 0 && pointerType === 'mouse') open();
        else if (opened) close();
        else open();
      });

      on(document, 'pointerdown', event => {
        if (!button.contains(event.target) && !tip.contains(event.target)) {
          close();
        }
      });

      on(document, 'keydown', event => {
        if (event.key === 'Escape') close();
      });

      on(window, 'resize', position);
      on(window, 'scroll', position, { capture: true, passive: true });

      if (window.visualViewport) {
        on(window.visualViewport, 'resize', position);
        on(window.visualViewport, 'scroll', position);
      }

      this.closeTooltip = close;
      this.cleanupTooltip = () => {
        close();
        button.hidden = true;
        this.append(tip);
        this.cleanupTooltip = null;
      };
    }

    disconnectedCallback() {
      this.cleanupTooltip?.();
      this.controller?.abort();
    }
  });
}